import React from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '@/context/ThemeContext';
import { motion, AnimatePresence } from 'framer-motion';
import { X, DownloadSimple, SealCheck, ShareNetwork } from '@phosphor-icons/react';
import { toast } from 'sonner';

const LOGO = "https://customer-assets.emergentagent.com/job_debt-cycle/artifacts/gzyrfqiy_diametor_logo_white-removebg-preview.png";
const CURRENCY = '\u20B9';
const SUPPORT_EMAIL = 'support@diametor.com';
const SUPPORT_PHONE = '+91-9072336912';
const NOC_DISCLAIMER = 'NOCs or Documents pledged or related to the amounts settled has to be requested and collected personally or directly from the debt provider, creditor, financial institutions or banks and As a Facilitator Platform we "Diametor" or Its Partners or Stake holders are never or no longer responsible or liable for any damage or delay or problems that may arise now or later in the future.';

export const certTitle = (cert) => cert?.cert_type === 'payment' ? 'CERTIFICATE OF PAYMENT'
  : cert?.cert_type === 'loan_closure' ? 'CERTIFICATE OF LOAN CLOSURE'
  : 'CERTIFICATE OF DISCHARGE';
const certSubtitle = (cert) => cert?.cert_type === 'payment'
  ? 'proves the following payables were paid to your creditors via netting'
  : cert?.cert_type === 'loan_closure'
  ? 'certifies that the following bank/NBFC loan was fully paid off via inter-cycle netting'
  : 'certifies the following receivables/obligations were discharged via netting';
const feePct = (cert) => (cert?.fee_pct != null ? cert.fee_pct : (cert?.cert_type === 'payment' ? 10 : 5));
const dirLabel = (d) => d.role === 'loan' ? 'Loan with' : d.role === 'debtor' ? 'Paid to' : 'Received from';

function closureRowsHtml(cert) {
  const c = cert.closure;
  if (!c) return '';
  const rows = [
    ['Amount Paid', `${CURRENCY}${(c.amount || 0).toLocaleString()}`],
    ['Transaction Reference', c.reference || '—'],
    ['Payment Mode', c.mode || '—'],
    ['Paid On', `${c.paid_date || ''} ${c.paid_time || ''}`.trim() || '—'],
    c.particulars ? ['Particulars', c.particulars] : null,
  ].filter(Boolean);
  return `<table style="width:100%;font-size:13px;margin:0 0 18px;border-collapse:collapse;">
    <tr><td colspan="2" style="padding:6px 0;font-weight:bold;color:#3f3f46;">Loan Closure Payment Details</td></tr>
    ${rows.map(([k, v]) => `<tr><td style="padding:4px 12px 4px 0;color:#71717a;">${k}</td><td style="padding:4px 0;font-weight:bold;">${v}</td></tr>`).join('')}
  </table>`;
}

function partyRow(label, name, email, phone) {
  const contact = [email, phone].filter(Boolean).join(' &middot; ');
  return `<tr>
    <td style="padding:4px 12px 4px 0;color:#71717a;white-space:nowrap;vertical-align:top;">${label}</td>
    <td style="padding:4px 0;"><strong>${name || '—'}</strong>${contact ? `<br/><span style="color:#52525b;font-size:12px;">${contact}</span>` : ''}</td>
  </tr>`;
}

function buildHtml(cert) {
  const rows = (cert.cleared_debts || []).map(d => `
    <tr>
      <td style="padding:8px 12px;border:1px solid #e4e4e7;">${dirLabel(d)}</td>
      <td style="padding:8px 12px;border:1px solid #e4e4e7;">${d.counterparty}${(d.counterparty_email || d.counterparty_phone) ? `<br/><span style="color:#71717a;font-size:11px;">${[d.counterparty_email, d.counterparty_phone].filter(Boolean).join(' &middot; ')}</span>` : ''}</td>
      <td style="padding:8px 12px;border:1px solid #e4e4e7;text-align:right;">${CURRENCY}${(d.amount || 0).toLocaleString()}</td>
    </tr>`).join('');
  const partiesRows = partyRow(cert.cert_type === 'payment' ? 'Certificate Holder (Payer)' : 'Certificate Holder (Payee)', cert.user_name, cert.user_email, cert.user_phone);
  return `<!doctype html><html><head><meta charset="utf-8"><title>${certTitle(cert)}</title></head>
  <body style="font-family:Arial,Helvetica,sans-serif;color:#18181b;max-width:760px;margin:32px auto;padding:0 24px;">
    <div style="text-align:center;border-bottom:3px solid #FACC15;padding-bottom:16px;margin-bottom:24px;">
      <img src="${LOGO}" alt="Diametor" style="height:46px;"/>
      <h1 style="font-size:22px;letter-spacing:1px;margin:12px 0 4px;">ELECTRONIC ${certTitle(cert)}</h1>
      <p style="color:#71717a;font-size:12px;margin:0;">Certificate ID: ${cert.certificate_id}</p>
    </div>
    <p style="font-size:14px;">This electronically ${certSubtitle(cert)} for <strong>${cert.user_name}</strong>, executed via Diametor's multilateral netting engine.</p>
    <table style="width:100%;font-size:13px;margin:12px 0 18px;border-collapse:collapse;">
      ${partiesRows}
    </table>
    <table style="width:100%;border-collapse:collapse;margin:18px 0;font-size:13px;">
      <thead><tr style="background:#fafafa;">
        <th style="padding:8px 12px;border:1px solid #e4e4e7;text-align:left;">Direction</th>
        <th style="padding:8px 12px;border:1px solid #e4e4e7;text-align:left;">Counterparty</th>
        <th style="padding:8px 12px;border:1px solid #e4e4e7;text-align:right;">Amount Cleared</th>
      </tr></thead>
      <tbody>${rows}</tbody>
    </table>
    <table style="width:100%;font-size:13px;margin-bottom:18px;">
      <tr><td style="padding:4px 0;">Total Debt Cleared</td><td style="text-align:right;font-weight:bold;">${CURRENCY}${(cert.total_cleared || 0).toLocaleString()}</td></tr>
      ${cert.received_amount ? `<tr><td style="padding:4px 0;">Amount Received (credited)</td><td style="text-align:right;font-weight:bold;">${CURRENCY}${cert.received_amount.toLocaleString()}</td></tr>` : ''}
      <tr><td style="padding:4px 0;">Platform Processing Fee (${feePct(cert)}%)</td><td style="text-align:right;font-weight:bold;">${CURRENCY}${(cert.fee_paid || 0).toLocaleString()}</td></tr>
      <tr><td style="padding:4px 0;">Transaction Timestamp</td><td style="text-align:right;">${cert.issued_at ? new Date(cert.issued_at).toLocaleString() : ''}</td></tr>
    </table>
    ${cert.cert_type === 'loan_closure' ? closureRowsHtml(cert) : ''}
    <div style="background:#fafafa;border-left:3px solid #FACC15;padding:12px 16px;font-size:12px;font-style:italic;color:#3f3f46;">
      ${cert.clause}
    </div>
    <p style="font-size:12px;color:#3f3f46;margin-top:16px;text-align:center;">If there is any dispute, kindly contact <strong>${SUPPORT_EMAIL}</strong> or call us at <strong>${SUPPORT_PHONE}</strong>.</p>
    <div style="background:#fff7ed;border:1px solid #fed7aa;border-radius:4px;padding:12px 16px;font-size:11px;color:#9a3412;margin-top:14px;"><strong>Disclaimer:</strong> ${NOC_DISCLAIMER}</div>
    <p style="font-size:11px;color:#a1a1aa;margin-top:8px;text-align:center;">No physical signature is required — this discharge is electronically processed and logged with an immutable audit trail under Section 65B of the Indian Evidence Act.</p>
  </body></html>`;
}

export function downloadCertificate(cert) {
  const w = window.open('', '_blank');
  if (!w) return;
  w.document.write(buildHtml(cert));
  w.document.close();
  setTimeout(() => { w.focus(); w.print(); }, 400);
}

export default function CertificateModal({ cert, open, onClose }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  if (!cert) return null;

  const shareLink = `${window.location.origin}/certificate/${cert.certificate_id}`;
  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: 'Diametor Certificate of Discharge', url: shareLink });
      } else {
        await navigator.clipboard.writeText(shareLink);
        toast.success('Shareable verification link copied');
      }
    } catch { /* user cancelled */ }
  };

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }}
            className={`relative w-full max-w-lg max-h-[92vh] overflow-y-auto rounded-md border ${isDark ? 'bg-zinc-950 border-zinc-800' : 'bg-white border-zinc-200'}`}
            data-testid="certificate-modal">
            <div className={`p-5 border-b flex items-center justify-between ${isDark ? 'border-zinc-800' : 'border-zinc-200'}`}>
              <div className="flex items-center gap-2">
                <SealCheck size={22} weight="bold" className="text-yellow-400" />
                <h3 className={`font-outfit text-base font-semibold ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>{cert.cert_type === 'payment' ? 'Certificate of Payment' : cert.cert_type === 'loan_closure' ? 'Certificate of Loan Closure' : 'Certificate of Discharge'}</h3>
              </div>
              <button onClick={onClose} className={`p-1 rounded-sm ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}><X size={18} weight="bold" /></button>
            </div>
            <div className="p-5 space-y-3">
              <p className={`font-ibm text-xs ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>Certificate ID: {cert.certificate_id}</p>

              {/* Parties */}
              <div className={`rounded-sm border p-3 space-y-2 ${isDark ? 'border-zinc-800 bg-zinc-900/50' : 'border-zinc-200 bg-zinc-50'}`} data-testid="cert-parties">
                <p className={`font-ibm text-[10px] font-semibold uppercase tracking-[0.2em] ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Parties</p>
                <div>
                  <p className={`font-ibm text-[10px] uppercase ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>{cert.cert_type === 'payment' ? 'Certificate Holder (Payer)' : 'Certificate Holder (Payee)'}</p>
                  <p className={`font-ibm text-sm font-semibold ${isDark ? 'text-zinc-100' : 'text-zinc-900'}`}>{cert.user_name}</p>
                  {(cert.user_email || cert.user_phone) && <p className={`font-ibm text-[11px] ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>{[cert.user_email, cert.user_phone].filter(Boolean).join(' · ')}</p>}
                </div>
                {(cert.cleared_debts || []).map((d, i) => (
                  <div key={i}>
                    <p className={`font-ibm text-[10px] uppercase ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>{dirLabel(d)}</p>
                    <p className={`font-ibm text-sm font-semibold ${isDark ? 'text-zinc-100' : 'text-zinc-900'}`}>{d.counterparty}</p>
                    {(d.counterparty_email || d.counterparty_phone) && <p className={`font-ibm text-[11px] ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>{[d.counterparty_email, d.counterparty_phone].filter(Boolean).join(' · ')}</p>}
                  </div>
                ))}
              </div>

              <div className="space-y-1.5">
                {(cert.cleared_debts || []).map((d, i) => (
                  <div key={i} className={`flex justify-between text-sm font-ibm ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                    <span>{d.role === 'debtor' ? 'You owed' : 'Owed to you by'} {d.counterparty}</span>
                    <span className="font-semibold">{CURRENCY}{(d.amount || 0).toLocaleString()}</span>
                  </div>
                ))}
              </div>
              <div className={`border-t pt-3 space-y-1 ${isDark ? 'border-zinc-800' : 'border-zinc-200'}`}>
                <div className="flex justify-between text-sm font-ibm"><span className={isDark ? 'text-zinc-400' : 'text-zinc-600'}>Total Cleared</span><span className="font-bold text-green-500">{CURRENCY}{(cert.total_cleared || 0).toLocaleString()}</span></div>
                {cert.received_amount > 0 && <div className="flex justify-between text-sm font-ibm"><span className={isDark ? 'text-zinc-400' : 'text-zinc-600'}>Received</span><span className="font-bold text-green-500">{CURRENCY}{cert.received_amount.toLocaleString()}</span></div>}
                <div className="flex justify-between text-sm font-ibm"><span className={isDark ? 'text-zinc-400' : 'text-zinc-600'}>Processing Fee ({feePct(cert)}%)</span><span className={`font-semibold ${isDark ? 'text-zinc-200' : 'text-zinc-800'}`}>{CURRENCY}{(cert.fee_paid || 0).toLocaleString()}</span></div>
              </div>
              {cert.cert_type === 'loan_closure' && cert.closure && (
                <div className={`rounded-sm border p-3 space-y-1 ${isDark ? 'border-zinc-800 bg-zinc-900/50' : 'border-zinc-200 bg-zinc-50'}`} data-testid="cert-closure-details">
                  <p className={`font-ibm text-[10px] font-semibold uppercase tracking-[0.2em] ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Loan Closure Payment Details</p>
                  {[['Amount Paid', `${CURRENCY}${(cert.closure.amount || 0).toLocaleString()}`], ['Reference', cert.closure.reference || '—'], ['Mode', cert.closure.mode || '—'], ['Paid On', `${cert.closure.paid_date || ''} ${cert.closure.paid_time || ''}`.trim() || '—'], ...(cert.closure.particulars ? [['Particulars', cert.closure.particulars]] : [])].map(([k, v], i) => (
                    <div key={i} className="flex justify-between text-sm font-ibm"><span className={isDark ? 'text-zinc-400' : 'text-zinc-600'}>{k}</span><span className={`font-semibold ${isDark ? 'text-zinc-200' : 'text-zinc-800'}`}>{v}</span></div>
                  ))}
                </div>
              )}
              <p className={`font-ibm text-[11px] italic p-3 rounded-sm border ${isDark ? 'border-zinc-800 bg-zinc-900/50 text-zinc-400' : 'border-zinc-200 bg-zinc-50 text-zinc-600'}`}>{cert.clause}</p>
              <p className={`font-ibm text-[11px] text-center ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`} data-testid="cert-dispute-note">If there is any dispute, kindly contact <strong>{SUPPORT_EMAIL}</strong> or call us at <strong>{SUPPORT_PHONE}</strong>.</p>
              <p className={`font-ibm text-[10px] leading-relaxed p-3 rounded-sm border ${isDark ? 'border-orange-400/30 bg-orange-400/5 text-orange-300/90' : 'border-orange-200 bg-orange-50 text-orange-800'}`} data-testid="cert-noc-disclaimer"><strong>Disclaimer:</strong> {NOC_DISCLAIMER}</p>
              <div className="flex gap-2">
                <button onClick={() => downloadCertificate(cert)} className="flex-1 h-11 bg-yellow-400 text-zinc-950 font-ibm text-sm font-semibold rounded-sm hover:bg-yellow-500 flex items-center justify-center gap-2" data-testid="download-certificate-btn">
                  <DownloadSimple size={16} weight="bold" /> Download
                </button>
                <button onClick={handleShare} className={`h-11 px-4 font-ibm text-sm font-semibold rounded-sm border flex items-center justify-center gap-2 ${isDark ? 'border-zinc-700 text-zinc-200 hover:border-yellow-400/50' : 'border-zinc-200 text-zinc-800 hover:border-yellow-400/50'}`} data-testid="share-certificate-btn">
                  <ShareNetwork size={16} weight="bold" /> Share
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
}
